import {
  startAuthentication,
  startRegistration,
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";

/**
 * The bit of the WebAuthn ceremony that has to run in the browser.
 *
 * Written against Better Auth's endpoints rather than through
 * `@better-auth/passkey/client` for one specific reason: that client builds the
 * verify request as `{ body: { response }, ...opts.fetchOptions }`, so anything
 * passed through `fetchOptions.body` replaces the response instead of joining
 * it. The OAuth continuation depends on sending `oauth_query` alongside the
 * response in the same body, which that shape cannot express.
 *
 *   getPasskeyActions (@better-auth/passkey/client):
 *     signIn.passkey: (opts?: { autoFill?: boolean; fetchOptions?: ClientFetchOption },
 *                      options?: ClientFetchOption) => Promise<...>
 *
 * Everything else about the ceremony is unchanged: the options come from the
 * server, the challenge cookie is the server's, and verification is the
 * server's.
 */

export type PageData<T> = T & { copy: Record<string, string> };

export function pageData<T>(): PageData<T> {
  const el = document.getElementById("page-data");
  if (!el?.textContent) throw new Error("page data missing");
  return JSON.parse(el.textContent) as PageData<T>;
}

export type ApiResult = {
  ok: boolean;
  status: number;
  data: Record<string, unknown> | null;
  message?: string;
  requestId?: string;
  /** A machine-readable refusal code, when the server sent one. */
  code?: string;
};

/**
 * The result of a WebAuthn ceremony plus the call that follows it.
 *
 * `cancelled` separates the two things that used to look alike to a caller:
 * the ceremony never completed (the person dismissed the prompt, the browser
 * aborted the conditional request, no credential matched on this device), and
 * the ceremony completed but the server refused what it produced.
 *
 * The first is ordinary and must stay silent on the conditional path; a message
 * there would put an error on a page where nothing went wrong. The second must
 * never be silent, and was: on login.human.tech the browser offered a passkey
 * registered against login-staging, the server refused it, and the page did
 * nothing at all (issue #353).
 */
export type CeremonyResult = ApiResult & { cancelled: boolean };

export async function api(path: string, init?: RequestInit): Promise<ApiResult> {
  const res = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  let data: Record<string, unknown> | null = null;
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    data = null;
  }
  const message =
    typeof data?.message === "string"
      ? data.message
      : typeof (data?.error as { message?: string } | undefined)?.message === "string"
        ? (data!.error as { message: string }).message
        : undefined;
  const code =
    typeof data?.code === "string"
      ? data.code
      : typeof (data?.error as { code?: string } | undefined)?.code === "string"
        ? (data!.error as { code: string }).code
        : typeof data?.error === "string"
          ? data.error
          : undefined;

  return {
    ok: res.ok,
    status: res.status,
    data,
    message,
    code,
    requestId:
      typeof data?.request_id === "string"
        ? data.request_id
        : (res.headers.get("x-request-id") ?? undefined),
  };
}

/** Every sign-in path ends the same way: follow the redirect the server gives. */
export function followRedirect(data: Record<string, unknown> | null, fallback: string): void {
  const url = typeof data?.url === "string" ? data.url : null;
  window.location.assign(url ?? fallback);
}

/**
 * Runs the ceremony against options the server already minted, rather than
 * asking for fresh ones. Used by the "Continue" path on the sign-in page, where
 * the options carry the account's own credential ids so the browser shows a
 * picker instead of an empty prompt.
 */
export async function assertWithOptions(
  optionsJSON: Record<string, unknown>,
  oauthQuery?: string,
): Promise<CeremonyResult> {
  let assertion;
  try {
    assertion = await startAuthentication({ optionsJSON: optionsJSON as never });
  } catch {
    // Dismissed, or no credential on this device matched. Not a refusal.
    return { ok: false, status: 0, data: null, cancelled: true };
  }
  const { clientExtensionResults: _drop, ...response } = assertion;
  const result = await api("/api/auth/passkey/verify-authentication", {
    method: "POST",
    body: JSON.stringify({ response, ...(oauthQuery ? { oauth_query: oauthQuery } : {}) }),
  });
  return { ...result, cancelled: false };
}

export async function assertPasskey(opts: {
  autoFill?: boolean;
  oauthQuery?: string;
  signal?: AbortSignal;
  /**
   * Ask the authenticator to verify the person, not merely to be present. Only
   * ever tightens the request; the refusal that binds is server-side, on the
   * flag in the signed authenticator data.
   */
  stepUp?: boolean;
}): Promise<CeremonyResult> {
  const options = await api(
    `/api/auth/passkey/generate-authenticate-options${opts.stepUp ? "?stepUp=1" : ""}`,
  );
  // No options means no ceremony, so nothing the person did failed.
  if (!options.ok || !options.data) return { ...options, cancelled: true };

  /**
   * `startAuthentication` takes no `AbortSignal`: the library owns the
   * ceremony's controller through `WebAuthnAbortService`, because only one
   * WebAuthn request can be outstanding at a time and it has to be able to
   * cancel the previous one itself. So an abort from our side is delivered the
   * way the library supports, by cancelling the outstanding ceremony.
   */
  if (opts.signal) {
    if (opts.signal.aborted) WebAuthnAbortService.cancelCeremony();
    else opts.signal.addEventListener("abort", () => WebAuthnAbortService.cancelCeremony(), { once: true });
  }

  let assertion;
  try {
    assertion = await startAuthentication({
      optionsJSON: options.data as never,
      useBrowserAutofill: opts.autoFill === true,
    });
  } catch {
    // Dismissed, aborted, or nothing on this device matched. The person did not
    // fail at anything, so the caller stays quiet.
    return { ok: false, status: 0, data: null, cancelled: true };
  }
  const { clientExtensionResults: _drop, ...response } = assertion;

  const result = await api("/api/auth/passkey/verify-authentication", {
    method: "POST",
    body: JSON.stringify({
      response,
      ...(opts.oauthQuery ? { oauth_query: opts.oauthQuery } : {}),
    }),
  });
  return { ...result, cancelled: false };
}

export async function registerPasskey(opts: {
  /** The join token, for a person who has no account yet. */
  context?: string;
  createSession?: boolean;
  oauthQuery?: string;
}): Promise<ApiResult> {
  const query = new URLSearchParams();
  if (opts.context) query.set("context", opts.context);
  const options = await api(
    `/api/auth/passkey/generate-register-options${query.size ? `?${query}` : ""}`,
  );
  if (!options.ok || !options.data) return options;

  const attestation = await startRegistration({ optionsJSON: options.data as never });
  const { clientExtensionResults: _drop, ...response } = attestation;

  return api("/api/auth/passkey/verify-registration", {
    method: "POST",
    body: JSON.stringify({
      response,
      ...(opts.createSession ? { createSession: true } : {}),
      ...(opts.oauthQuery ? { oauth_query: opts.oauthQuery } : {}),
    }),
  });
}

export { browserSupportsWebAuthnAutofill };

/**
 * The sentence, and under it the reference id when there is one (plan G7).
 *
 * A person who can read an id off the screen can be helped; one who cannot has
 * to describe what they saw. The id comes from the error body the server sent,
 * which carries it on every JSON refusal including Better Auth's own.
 */
export function status(
  text: string,
  kind: "info" | "error" = "info",
  requestId?: string,
  emphasis: string[] = [],
): void {
  const el = document.getElementById("status");
  if (el) {
    setMessage(el, text, emphasis);
    el.dataset.kind = kind;
  }
  showReference(requestId);
}

/**
 * A sentence with the phrases that matter picked out.
 *
 * Built out of text nodes and `<strong>` rather than assigned as HTML: the
 * sentence is a server message and the phrases are addresses out of a server
 * response, so neither is ours to trust with markup. Nothing here can inject
 * an element that was not created here.
 *
 * It exists because a refusal reads in the page's own ink now (see the
 * `.status[data-kind="error"]` rule in src/pages/layout.ts): a whole paragraph
 * in red said "something is broken" about a page that is working correctly, so
 * the weight goes on the one or two phrases the person has to act on instead.
 */
export function setMessage(
  el: Element,
  text: string,
  emphasis: string[] = [],
  /**
   * The one emphasised phrase that names what is wrong, if any. It is the only
   * thing on these pages painted `--error`: on the wrong-account refusal the
   * two addresses are both emphasised, but one of them is the address the
   * person should be using, and marking the right answer red says it is a
   * fault when it is the way out.
   */
  fault?: string,
): void {
  el.textContent = "";
  const wanted = emphasis.filter((phrase) => phrase.length > 0 && text.includes(phrase));
  if (wanted.length === 0) {
    el.textContent = text;
    return;
  }
  let rest = text;
  while (rest.length > 0) {
    // The phrase that starts earliest in what is left, so two phrases in one
    // sentence come out in the order they are written.
    let at = -1;
    let hit = "";
    for (const phrase of wanted) {
      const found = rest.indexOf(phrase);
      if (found !== -1 && (at === -1 || found < at)) {
        at = found;
        hit = phrase;
      }
    }
    if (at === -1) {
      el.append(rest);
      return;
    }
    if (at > 0) el.append(rest.slice(0, at));
    const strong = document.createElement("strong");
    strong.textContent = hit;
    if (fault !== undefined && hit === fault) strong.className = "fault";
    el.append(strong);
    rest = rest.slice(at + hit.length);
  }
}

/** The reference id under a refusal: small, last, and only when there is one. */
export function showReference(requestId?: string): void {
  const ref = document.getElementById("request-ref");
  if (!ref) return;
  const template = referenceTemplate();
  if (requestId && template) {
    ref.textContent = template.replace("{id}", requestId);
    ref.hidden = false;
  } else {
    ref.textContent = "";
    ref.hidden = true;
  }
}

let cachedTemplate: string | null | undefined;
function referenceTemplate(): string | null {
  if (cachedTemplate === undefined) {
    const el = document.getElementById("page-data");
    const parsed = el?.textContent ? (JSON.parse(el.textContent) as PageData<unknown>) : null;
    cachedTemplate = parsed?.copy?.referenceTemplate ?? null;
  }
  return cachedTemplate;
}

export function busy(button: HTMLButtonElement | null, on: boolean, label?: string): void {
  if (!button) return;
  button.disabled = on;
  if (on && label) {
    button.dataset.label = button.textContent ?? "";
    button.textContent = label;
  } else if (!on && button.dataset.label) {
    button.textContent = button.dataset.label;
    delete button.dataset.label;
  }
}
